import React from "react";
import { NavLink, useLocation, Link, useNavigate } from "react-router-dom";
import { Activity, Radar, Bookmark, Bell, Settings, Download, Power, Crosshair, KeyRound, CreditCard, LogOut, User, Globe2, Lock, Crown, Home, RotateCcw, X } from "lucide-react";
import { useScannerState, useStartScanner, useStopScanner, useRunScanOnce, useRestartScanner, useVaultStatus, useVaultLock, useOpenScalpBrowsers, useCloseAllScalpBrowsers } from "@/lib/queries";
import { toast } from "sonner";
import { formatRelative } from "@/lib/cr";
import { useAuth } from "@/lib/auth";
import { useRuntime } from "@/lib/runtime";

const navItems = [
  { to: "/app", label: "Radar", icon: Radar, testId: "nav-radar", exact: true },
  { to: "/app/watchlist", label: "Watchlist", icon: Bookmark, testId: "nav-watchlist" },
  { to: "/app/alerts", label: "Alerts", icon: Bell, testId: "nav-alerts" },
  { to: "/app/scalp", label: "Scalp", icon: Crosshair, testId: "nav-scalp", danger: true, desktopOnly: true },
  { to: "/app/accounts", label: "Accounts", icon: KeyRound, testId: "nav-accounts", desktopOnly: true },
  { to: "/app/payments", label: "Payments", icon: CreditCard, testId: "nav-payments", desktopOnly: true },
  { to: "/app/shipping", label: "Shipping", icon: Home, testId: "nav-shipping", desktopOnly: true },
  { to: "/app/proxies", label: "Proxies", icon: Globe2, testId: "nav-proxies", desktopOnly: true },
  { to: "/app/settings", label: "Settings", icon: Settings, testId: "nav-settings" },
  { to: "/app/desktop", label: "Desktop", icon: Download, testId: "nav-desktop" },
];

export default function Header() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { data: scanner } = useScannerState();
  const { data: vaultStatus } = useVaultStatus();
  const vaultLock = useVaultLock();
  const startMutation = useStartScanner();
  const stopMutation = useStopScanner();
  const { data: openBrowsers } = useOpenScalpBrowsers();
  const closeAllBrowsers = useCloseAllScalpBrowsers();
  const runOnce = useRunScanOnce();
  const restartScanner = useRestartScanner();
  const { is_desktop } = useRuntime();

  const active = scanner?.active;
  // Stale-heartbeat detection: if the loop is "active" but hasn't beat
  // in > 3× its interval (default 60 s), the background task is wedged.
  // The user clicks "Restart Scanner" to respawn it without restarting
  // the app — used to be impossible, which is why the previous build
  // sat at "SCANNING / Last 3d ago" for days.
  const isStuck = React.useMemo(() => {
    if (!active) return false;
    const hb = scanner?.last_heartbeat_at;
    if (!hb) return false;
    const intervalMs = (scanner?.interval_seconds || 20) * 1000;
    return (Date.now() - new Date(hb).getTime()) > intervalMs * 3;
  }, [active, scanner?.last_heartbeat_at, scanner?.interval_seconds]);
  const [menuOpen, setMenuOpen] = React.useState(false);
  const menuRef = React.useRef(null);

  React.useEffect(() => {
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const handleLogout = async () => {
    // Navigate first so we leave the protected shell before the cookie is cleared
    // (otherwise ProtectedRoute re-renders and races to /login).
    navigate("/", { replace: true });
    await logout();
  };

  return (
    <header className="h-14 border-b border-[#262626] flex items-center justify-between px-4 shrink-0 bg-[#050505]/95 backdrop-blur-sm sticky top-0 z-30">
      <div className="flex items-center gap-6">
        <div className="flex items-center gap-2.5" data-testid="brand">
          <div className="relative w-8 h-8 border border-[#EAB308] flex items-center justify-center bg-black">
            <Radar className="w-4 h-4 text-[#EAB308]" strokeWidth={2.5} />
            <div className="scanline" />
          </div>
          <div className="leading-tight">
            <div className="font-heading text-base font-black tracking-tighter uppercase">
              Card<span className="text-[#EAB308]">Radar</span>
            </div>
            <div className="text-[9px] uppercase tracking-[0.25em] text-[#737373] font-mono -mt-0.5">
              TCG Market Scanner
            </div>
          </div>
        </div>

        <nav className="flex items-center gap-1" data-testid="primary-nav">
          {navItems.map(({ to, label, icon: Icon, testId, danger, exact, desktopOnly }) => {
            const locked = desktopOnly && !is_desktop;
            return (
            <NavLink
              key={to}
              to={to}
              end={exact === true}
              data-testid={testId}
              title={locked ? "Desktop app required — click to learn more" : undefined}
              className={({ isActive }) =>
                `flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] uppercase tracking-wider font-semibold border-b-2 transition-colors ${
                  isActive
                    ? `text-white ${danger ? "border-[#EF4444]" : "border-[#EAB308]"}`
                    : locked
                    ? "text-[#525252] border-transparent hover:text-[#A3A3A3] hover:border-[#262626]"
                    : `${danger ? "text-[#FCA5A5]" : "text-[#A3A3A3]"} border-transparent hover:text-white hover:border-[#262626]`
                }`
              }
            >
              <Icon className="w-3.5 h-3.5" strokeWidth={2.2} />
              {label}
              {locked && <Lock className="w-2.5 h-2.5 text-[#525252]" strokeWidth={2.5} />}
            </NavLink>
            );
          })}
        </nav>
      </div>

      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2 px-3 py-1.5 border border-[#262626] bg-[#0f0f0f]" data-testid="scanner-status">
          <span className={`pulse-dot ${active ? "" : "idle"} ${isStuck ? "stuck" : ""}`} />
          <div className="leading-tight">
            <div className={`text-[10px] uppercase tracking-[0.18em] font-bold ${isStuck ? "text-[#EF4444]" : "text-[#A3A3A3]"}`}>
              {isStuck ? "Stuck" : active ? "Scanning" : "Idle"}
            </div>
            <div className="text-[10px] font-mono text-[#737373]">
              last {formatRelative(scanner?.last_scan_at)}
            </div>
          </div>
        </div>

        {isStuck && (
          <button
            onClick={() => restartScanner.mutate()}
            data-testid="restart-scanner-btn"
            disabled={restartScanner.isPending}
            className="px-3 py-2 text-xs uppercase font-bold tracking-wider border border-[#EF4444]/40 text-[#EF4444] hover:bg-[#EF4444]/10 transition-all flex items-center gap-1.5 disabled:opacity-50"
            title="Background scanner appears wedged. Click to respawn it without restarting the app."
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Restart
          </button>
        )}

        <button
          onClick={() => runOnce.mutate()}
          data-testid="run-scan-now-btn"
          className="px-3 py-2 text-xs uppercase font-bold tracking-wider border border-[#262626] hover:border-[#A3A3A3] hover:bg-[#1A1A1A] transition-all flex items-center gap-1.5"
          title="Run a single scan now"
        >
          <Activity className="w-3.5 h-3.5" />
          Scan now
        </button>

        {openBrowsers?.count > 0 && (
          <button
            onClick={() => closeAllBrowsers.mutate(undefined, {
              onSuccess: (r) => toast.success(`Closed ${r.closed} scalp browser${r.closed === 1 ? "" : "s"}`),
            })}
            data-testid="close-all-scalp-browsers-btn"
            disabled={closeAllBrowsers.isPending}
            className="px-3 py-2 text-xs uppercase font-bold tracking-wider border border-[#F59E0B]/40 text-[#F59E0B] hover:bg-[#F59E0B]/10 transition-all flex items-center gap-1.5 disabled:opacity-50"
            title={`${openBrowsers.count} scalp browser${openBrowsers.count === 1 ? "" : "s"} open (${openBrowsers.retailers.join(", ")}). Click to close all.`}
          >
            <X className="w-3.5 h-3.5" />
            Close {openBrowsers.count} {openBrowsers.count === 1 ? "browser" : "browsers"}
          </button>
        )}

        {active ? (
          <button
            onClick={() => stopMutation.mutate()}
            data-testid="stop-scanner-btn"
            className="px-4 py-2 text-xs uppercase font-bold tracking-wider border border-[#EF4444]/40 text-[#EF4444] hover:bg-[#EF4444]/10 transition-all flex items-center gap-1.5"
          >
            <Power className="w-3.5 h-3.5" />
            Stop
          </button>
        ) : (
          <button
            onClick={() => startMutation.mutate(20)}
            data-testid="start-scanner-btn"
            className="px-4 py-2 text-xs uppercase font-bold tracking-wider bg-[#EAB308] text-black hover:bg-[#FDE047] transition-all flex items-center gap-1.5 shadow-[0_0_12px_rgba(234,179,8,0.25)]"
          >
            <Power className="w-3.5 h-3.5" />
            Start scan
          </button>
        )}

        {/* User menu */}
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setMenuOpen((v) => !v)}
            data-testid="user-menu-btn"
            className="w-9 h-9 border border-[#262626] hover:border-[#EAB308] flex items-center justify-center bg-[#0f0f0f] transition-colors"
          >
            <User className="w-4 h-4 text-[#A3A3A3]" />
          </button>
          {menuOpen && (
            <div className="absolute top-11 right-0 w-64 bg-[#0d0d0d] border border-[#262626] shadow-2xl py-2 z-50" data-testid="user-menu">
              <div className="px-3 py-2 border-b border-[#262626]">
                <div className="text-sm text-white font-semibold truncate">{user?.name || user?.email}</div>
                <div className="text-[11px] font-mono text-[#737373] truncate">{user?.email}</div>
                <div className="mt-2 flex items-center gap-1.5">
                  <span className={`w-1.5 h-1.5 rounded-full ${user?.subscription_status === "active" ? "bg-[#22C55E]" : "bg-[#737373]"}`} />
                  <span className="text-[10px] uppercase tracking-wider font-bold font-mono">
                    {user?.role === "admin" ? "Admin" : (user?.subscription_status || "inactive")}
                  </span>
                </div>
              </div>
              <Link
                to="/billing"
                onClick={() => setMenuOpen(false)}
                data-testid="user-menu-billing"
                className="block px-3 py-2 text-xs text-[#A3A3A3] hover:bg-[#1A1A1A] hover:text-white flex items-center gap-2"
              >
                <CreditCard className="w-3.5 h-3.5" /> Billing
              </Link>
              {user?.role === "admin" && (
                <Link
                  to="/admin"
                  onClick={() => setMenuOpen(false)}
                  data-testid="user-menu-admin"
                  className="block px-3 py-2 text-xs text-[#EF4444] hover:bg-[#EF4444]/10 flex items-center gap-2 font-bold"
                >
                  <Crown className="w-3.5 h-3.5" /> Admin portal
                </Link>
              )}
              {vaultStatus?.local && !vaultStatus?.locked && (
                <button
                  data-testid="user-menu-lock-vault"
                  onClick={() => { vaultLock.mutate(); setMenuOpen(false); }}
                  className="w-full text-left px-3 py-2 text-xs text-[#A3A3A3] hover:bg-[#1A1A1A] hover:text-white flex items-center gap-2"
                >
                  <Lock className="w-3.5 h-3.5" /> Lock vault
                </button>
              )}
              <button
                onClick={handleLogout}
                data-testid="user-menu-logout"
                className="w-full text-left px-3 py-2 text-xs text-[#EF4444] hover:bg-[#EF4444]/10 flex items-center gap-2"
              >
                <LogOut className="w-3.5 h-3.5" /> Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
